/**
 * @file App.tsx
 * @description Root application component with Redux Provider, Router, Auth Session Initialization, and Route Guards.
 * All page-level components are lazy-loaded for optimal code splitting.
 */
import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { notify } from './shared/utils/notify.js';

if (typeof window !== 'undefined') {
  (window as unknown as { toast: typeof toast; notify: typeof notify }).toast = toast;
  (window as unknown as { toast: typeof toast; notify: typeof notify }).notify = notify;
}

import { store } from './app/store.js';
import { useRefreshMutation } from './features/auth/api/authApi.js';
import { setCredentials, setInitialized } from './features/auth/model/authSlice.js';
import { ProtectedRoute } from './features/auth/ui/ProtectedRoute.js';

// ── Lazy-loaded page components for route-based code splitting ────────────────
const HomePage = lazy(() => import('./pages/home/HomePage.js').then(m => ({ default: m.HomePage })));
const ToolsPage = lazy(() => import('./pages/tools/ToolsPage.js').then(m => ({ default: m.ToolsPage })));
const LoginPage = lazy(() => import('./pages/auth/LoginPage.js').then(m => ({ default: m.LoginPage })));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage.js').then(m => ({ default: m.VerifyEmailPage })));
const ChartPreviewPage = lazy(() => import('./pages/data/ChartPreviewPage.js').then(m => ({ default: m.ChartPreviewPage })));
const StrategyBuilderPage = lazy(() => import('./pages/strategy/StrategyBuilderPage.js').then(m => ({ default: m.StrategyBuilderPage })));
const AdminPage = lazy(() => import('./pages/admin/AdminPage.js').then(m => ({ default: m.AdminPage })));
const StrategyListPage = lazy(() => import('./pages/strategy/StrategyListPage.js').then(m => ({ default: m.StrategyListPage })));
const BacktestReportPage = lazy(() => import('./pages/backtest/BacktestReportPage.js').then(m => ({ default: m.BacktestReportPage })));
const MqlExportPage = lazy(() => import('./pages/export/MqlExportPage.js').then(m => ({ default: m.MqlExportPage })));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage.js').then(m => ({ default: m.DashboardPage })));
const PricingPage = lazy(() => import('./pages/pricing/PricingPage.js').then(m => ({ default: m.PricingPage })));
const DepositPage = lazy(() => import('./pages/pricing/DepositPage.js').then(m => ({ default: m.DepositPage })));
const PaperTradingPage = lazy(() => import('./pages/paper/PaperTradingPage.js').then(m => ({ default: m.PaperTradingPage })));
const ContactPage = lazy(() => import('./pages/contact/ContactPage.js').then(m => ({ default: m.ContactPage })));
const NotFoundPage = lazy(() => import('./pages/errors/NotFoundPage.js').then(m => ({ default: m.NotFoundPage })));
const ErrorBoundary = lazy(() => import('./shared/components/ErrorBoundary.js').then(m => ({ default: m.ErrorBoundary })));

import { CookieConsentBanner } from './shared/components/CookieConsentBanner.js';
import type { LegalModalType } from './shared/components/LegalModal.js';
import { useState } from 'react';

const LegalModal = lazy(() => import('./shared/components/LegalModal.js').then(m => ({ default: m.LegalModal })));

/**
 * Minimal full-screen loading spinner shown while lazy chunks are downloading.
 * Uses only inline styles to avoid depending on CSS that hasn't loaded yet.
 */
const PageLoader = (): JSX.Element => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bybit-bg-canvas, #07090c)',
    }}
  >
    <div
      style={{
        width: 32,
        height: 32,
        border: '3px solid rgba(240, 185, 11, 0.15)',
        borderTopColor: '#f0b90b',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }}
    />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

/**
 * Silent session initializer — attempts token refresh on app startup
 */
const AuthInitializer = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const dispatch = useDispatch();
  const [refresh] = useRefreshMutation();

  useEffect(() => {
    let isMounted = true;

    const initAuth = async (): Promise<void> => {
      try {
        const response = await refresh().unwrap();
        if (isMounted && response.success && response.data) {
          dispatch(setCredentials(response.data));
        } else if (isMounted) {
          dispatch(setInitialized());
        }
      } catch {
        if (isMounted) {
          dispatch(setInitialized());
        }
      }
    };

    void initAuth();

    return () => {
      isMounted = false;
    };
  }, [dispatch, refresh]);

  return <>{children}</>;
};


/**
 * ScrollToTop - Automatically scrolls the window to the top (0,0) on every route change
 */
const ScrollToTop = (): null => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const AppContent = (): JSX.Element => {
  const [legalModal, setLegalModal] = useState<{ isOpen: boolean; type: LegalModalType }>({
    isOpen: false,
    type: 'cookies',
  });

  return (
    <BrowserRouter>
      <ScrollToTop />

      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ── Public Experience & Freemium Workspace Routes ──────────────── */}
          <Route path="/" element={<HomePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/tools/:toolName" element={<ToolsPage />} />
          <Route path="/data" element={<ChartPreviewPage />} />
          <Route path="/strategies/new" element={<StrategyBuilderPage />} />
          <Route path="/strategies/:id/backtest" element={<BacktestReportPage />} />
          <Route path="/backtest/results/:resultId" element={<BacktestReportPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* ── Protected Cloud & Account Routes ─────────────────────────── */}
          <Route
            path="/deposit"
            element={
              <ProtectedRoute>
                <DepositPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ErrorBoundary>
                    <DashboardPage />
                  </ErrorBoundary>
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/strategies"
            element={
              <ProtectedRoute>
                <StrategyListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/strategies/:id/edit"
            element={
              <ProtectedRoute>
                <StrategyBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/strategies/:id/export"
            element={
              <ProtectedRoute>
                <MqlExportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/paper-trading"
            element={
              <ProtectedRoute>
                <PaperTradingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <ErrorBoundary>
                    <AdminPage />
                  </ErrorBoundary>
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* ── 404 Fallback ──────────────────────────────────────────────── */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      {/* Global Cookie Consent Banner */}
      <CookieConsentBanner
        onOpenCookiePolicy={() => setLegalModal({ isOpen: true, type: 'cookies' })}
      />

      {/* Global Legal & Cookie Policy Modal */}
      {legalModal.isOpen && (
        <Suspense fallback={null}>
          <LegalModal
            isOpen={legalModal.isOpen}
            type={legalModal.type}
            onClose={() => setLegalModal((prev) => ({ ...prev, isOpen: false }))}
            onSelectType={(type) => setLegalModal({ isOpen: true, type })}
          />
        </Suspense>
      )}
    </BrowserRouter>
  );
};

const App = (): JSX.Element => {
  return (
    <Provider store={store}>
      <AuthInitializer>
        <AppContent />
      </AuthInitializer>
    </Provider>
  );
};

export default App;
